<template>
    <div class="tip-and-pay-form">
        <div>
            <label for="bill-tip-input">{{ translations.pay.tip }}</label>
            <CurrencyInput v-model="currentTip" :id="'bill-tip-input'" :disabled="disabled" :max="maxTip" />
        </div>
        <div>
            <label for="bill-received-input">{{ translations.pay.received }}</label>
            <CurrencyInput
                v-model="received"
                :id="'bill-received-input'"
                :disabled="disabled"
                :min="totalConsumption"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Misc } from "@/data/util/Misc";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref, watch } from "vue";

    import CurrencyInput from "@/components/elements/CurrencyInput.vue";

    // define props
    const props = defineProps<{
        disabled: boolean;
        totalConsumption: number;
        tip: number | null;
    }>();

    // define emits
    const emit = defineEmits(["update:tip"]);

    // define reactive variables
    const currentTip = ref(props.tip);
    const received = ref(props.tip === null ? null : Misc.roundCents(props.totalConsumption + (currentTip.value ?? 0)));
    const MAX_NUMBER = 9_999_999;
    const maxTip = computed(() => MAX_NUMBER - props.totalConsumption);

    // define methods
    watch(currentTip, () => {
        if (currentTip.value !== null && currentTip.value > maxTip.value) {
            currentTip.value = maxTip.value;
            received.value = Misc.roundCents(props.totalConsumption + currentTip.value);
        }
        received.value = Misc.roundCents(props.totalConsumption + (currentTip.value ?? 0));
        if (received.value < props.totalConsumption) {
            received.value = props.totalConsumption;
        }

        emit("update:tip", currentTip.value);
    });

    watch(received, () => {
        if (received.value === null || received.value === undefined) return;
        if (received.value > MAX_NUMBER) {
            received.value = MAX_NUMBER;
        }
        currentTip.value = Misc.roundCents(received.value - props.totalConsumption);
        if (currentTip.value < 0) {
            currentTip.value = 0;
        }
    });

    watch(maxTip, () => {
        if (currentTip.value != null && currentTip.value > maxTip.value) {
            currentTip.value = maxTip.value;
            received.value = Misc.roundCents(props.totalConsumption + currentTip.value);
        }
    });

    watch(
        () => props.totalConsumption,
        () => {
            received.value = Misc.roundCents(props.totalConsumption + (currentTip.value ?? 0));
        }
    );

    watch(
        () => props.tip,
        () => {
            currentTip.value = props.tip;
        }
    );
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .tip-and-pay-form {
        font-size: 14px;
        display: flex;
        flex-direction: column;
        align-items: center;

        > div {
            padding: 1em 0em;
            padding-right: 3em;
            display: flex;
            align-items: center;

            label {
                text-align: right;
                font-size: 14px;
                width: 5em;
                font-weight: 700;
                line-height: 1em;
                margin-bottom: 0;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                margin-right: 2em;

                color: colors.$fontColor;
                flex: 0 0 auto;
            }
        }

        @media only screen and (max-width: 1000px) {
            > div {
                display: block;
                padding: 1em 0;
                label {
                    display: block;
                    text-align: left;
                }

                span {
                    > input {
                        width: 100%;
                    }
                }
            }
        }
    }
</style>
